import type { SignupConfirmed } from '@epc/contracts';
import { useMutation } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { api } from '../../lib/api';
import { EmptyState, ErrorBox, Spinner } from '../../ui/feedback';
import { AuthLayout } from '../auth/AuthLayout';

/**
 * Подтверждение почты при самостоятельной регистрации (ГЛ-05): ссылка из письма создаёт компанию с пробным
 * периодом и приглашением администратора — дальше человек задаёт пароль. Ссылка одноразовая: запрос уходит
 * один раз при открытии страницы, повтор не нужен.
 */
export function SignupConfirmPage() {
  const { token } = useParams({ strict: false }) as { token: string };
  const started = useRef(false);
  const confirm = useMutation({
    mutationFn: () => api<SignupConfirmed>('/public/signup/confirm', { body: { token } }),
  });
  const { mutate } = confirm;
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    mutate();
  }, [mutate]);

  const linkClass =
    'inline-flex h-11 w-fit items-center rounded-md border border-line-strong px-4 text-[14px] font-medium text-ink hover:underline underline-offset-[3px]';

  if (confirm.isSuccess) {
    const r = confirm.data;
    return (
      <AuthLayout
        title="Почта подтверждена"
        subtitle={`Компания «${r.company}» создана. Пробный период — ${r.trialDays} дн. Осталось задать пароль и войти.`}
      >
        <div className="flex flex-col gap-4">
          <Link
            to={r.invitePath}
            className="inline-flex h-12 w-fit items-center rounded-md bg-accent px-5 text-[15px] font-semibold text-on-accent underline-offset-[3px] hover:underline"
          >
            Задать пароль и войти
          </Link>
          <p className="text-[13px] text-ink-2">
            Ссылка для пароля одноразовая. Если закроете страницу, не задав пароль, попросите нового
            приглашения у владельца портала.
          </p>
        </div>
      </AuthLayout>
    );
  }
  if (confirm.isError) {
    return (
      <AuthLayout title="Подтверждение регистрации">
        <div className="flex flex-col gap-4">
          <EmptyState title="Ссылка не сработала">
            Возможно, она устарела или уже использована. Если компания уже создана — войдите по приглашению из
            письма; иначе зарегистрируйтесь заново на главной странице.
          </EmptyState>
          <ErrorBox error={confirm.error} />
          <div className="flex flex-wrap gap-2">
            <Link to="/welcome" className={linkClass}>
              На главную
            </Link>
            <Link to="/login" className={linkClass}>
              Ко входу
            </Link>
          </div>
        </div>
      </AuthLayout>
    );
  }
  return (
    <AuthLayout title="Подтверждение регистрации">
      <Spinner label="Подтверждаем почту и создаём компанию" />
    </AuthLayout>
  );
}
